/**
 * Retry utility with exponential backoff for network operations.
 *
 * Fixes REFSIX-REACT-4EH, REFSIX-REACT-4EJ
 */

export interface RetryOptions {
    /** Maximum number of retry attempts (default: 3) */
    maxRetries?: number;
    /** Initial delay in milliseconds before first retry (default: 1000) */
    initialDelayMs?: number;
    /** Maximum delay in milliseconds between retries (default: 10000) */
    maxDelayMs?: number;
    /** Function to determine if an error should trigger a retry (default: retry on network/timeout errors) */
    shouldRetry?: (error: any) => boolean;
}

const defaultShouldRetry = (error: any): boolean => {
    // Retry on server errors (5xx) and specific client errors
    if (error?.response?.status) {
        const status = error.response.status;
        // Retry on 5xx server errors, 408 Request Timeout, 429 Too Many Requests
        return status >= 500 || status === 408 || status === 429;
    }

    // Check for status directly on the error (some APIs put it there)
    if (error?.status) {
        const status = error.status;
        return status >= 500 || status === 408 || status === 429;
    }

    // Retry on timeout or network errors
    const errorCode = error?.code;
    const errorMessage = error?.message?.toLowerCase() || "";

    return (
        errorCode === "ECONNABORTED" ||
        errorCode === "ETIMEDOUT" ||
        errorCode === "ECONNRESET" ||
        errorMessage.includes("timeout") ||
        errorMessage.includes("network error") ||
        errorMessage.includes("network request failed")
    );
};

const defaultOptions: Required<RetryOptions> = {
    maxRetries: 3,
    initialDelayMs: 1000,
    maxDelayMs: 10000,
    shouldRetry: defaultShouldRetry,
};

/**
 * Executes an async function with retry logic and exponential backoff.
 *
 * @param fn - The async function to execute
 * @param options - Configuration options for retry behavior
 * @returns The result of the function if successful
 * @throws The last error if all retries are exhausted
 *
 * @example
 * ```typescript
 * const data = await withRetry(
 *   () => fetchData(url),
 *   { maxRetries: 3, initialDelayMs: 1000 }
 * );
 * ```
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const opts = { ...defaultOptions, ...options };
    let lastError: any;

    for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Don't retry if this was the last attempt or if error isn't retryable
            if (attempt === opts.maxRetries || !opts.shouldRetry(error)) {
                throw error;
            }

            // Calculate delay with exponential backoff
            const delay = Math.min(
                opts.initialDelayMs * Math.pow(2, attempt),
                opts.maxDelayMs
            );

            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError;
}
